import { useAtom, useSetAtom } from 'jotai';

import SelectionState from '@/features/trends/components/selections-sidebar/state/selections.state';
import { currentChartAttributesAtom } from '@/features/trends/state/current-chart.state';
import { DeviceAttribute } from '@/features/trends/trends.types';
import { attributesEqual, dedupeAttributes } from '@/features/trends/trends.utils';

/**
 * Hook for managing device attribute selections in the current chart.
 * Provides methods to add, remove, toggle, check, and clear attribute selections.
 * All operations automatically handle deduplication using attribute equality checks.
 *
 * @returns Object containing methods for attribute selection management:
 *  - toggleAttribute: Add if not present, remove if present
 *  - clearAttributes: Remove all selections
 *  - addAttribute: Add multiple attributes (auto-deduplicates)
 *  - removeAttribute: Remove multiple attributes
 *  - hasAttribute: Check if an attribute is selected
 */
export const useAttributeSelection = () => {
  const [attributes, setAttributes] = useAtom(currentChartAttributesAtom);
  const setExpandedSections = useSetAtom(SelectionState.expandedSectionIds);
  const setSearchTerm = useSetAtom(SelectionState.searchTerm);

  /**
   * Adds one or more device attributes to the current selection.
   * Automatically deduplicates if attributes already exist.
   *
   * @param additions - Array of device attributes to add
   */
  const addAttributeAction = (additions: DeviceAttribute[]): void => {
    setAttributes(dedupeAttributes([...attributes, ...additions]));
  };

  /**
   * Removes one or more device attributes from the current selection.
   * Uses attribute equality comparison to identify matches.
   *
   * @param targets - Array of device attributes to remove
   */
  const removeAttributeAction = (targets: DeviceAttribute[]): void => {
    setAttributes(
      attributes.filter(
        (attr: DeviceAttribute) => !targets.some((target) => attributesEqual(attr, target))
      )
    );
  };

  /**
   * Toggles the selection state of a device attribute.
   * If the attribute is currently selected, it will be removed.
   * If the attribute is not selected, it will be added.
   *
   * @param attribute - The device attribute to toggle
   */
  const toggleAttributeAction = (attribute: DeviceAttribute): void => {
    if (hasAttribute(attribute)) {
      removeAttributeAction([attribute]);
    } else {
      addAttributeAction([attribute]);
    }
  };

  /**
   * Clears all device attributes from the current selection.
   */
  const clearAttributesAction = (): void => {
    setAttributes([]);
    setExpandedSections([]);
    setSearchTerm('');
  };

  /**
   * Checks if a specific device attribute is currently selected.
   *
   * @param attribute - The device attribute to check
   * @returns True if the attribute is in the current selection, false otherwise
   */
  const hasAttribute = (attribute: DeviceAttribute) => {
    return attributes.some((attr: DeviceAttribute) => attributesEqual(attr, attribute));
  };

  return {
    toggleAttributeAction,
    clearAttributesAction,
    addAttributeAction,
    removeAttributeAction,
    hasAttribute,
  };
};
